/**
 * Team Management wizard — "Add your team & squad".
 *
 * Orchestrates three steps for ONE team: Basics → Coaches → Squad.
 * Persists incrementally so skip-for-now is always safe — the team is
 * created the moment Basics completes; every later step enriches a
 * real team. Resume mode pre-fills an existing team and jumps to a
 * chosen step.
 *
 * The Coaches + Squad steps are imported from ./steps and are reused
 * by the future new-club wizard.
 *
 * See docs/features/team-management-wizard.md.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  createTeam,
  updateTeam,
  createPlayer,
  createCoach,
  createMember,
  getMembers,
  getPlayersAndLimit,
  linkGuardian,
  Team,
  Coach,
  Player,
} from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import { playerRoom, isPlayerLimitError } from '../../utils/playerLimit';
import ImageUploader from '../ImageUploader';
import { CoachesStep, SquadStep, parseSquadInput, ParsedSquadRow } from './steps';
import './TeamWizard.css';

export type WizardStep = 'basics' | 'coaches' | 'squad' | 'done';

// Back to a pasteable line, so names that didn't go in can be pasted again.
const squadLine = (p: ParsedSquadRow) => {
  const cells = [`${p.firstName} ${p.lastName}`.trim(), p.guardianName, p.guardianEmail];
  while (cells.length > 1 && !cells[cells.length - 1]) cells.pop();
  return cells.join(', ');
};

interface TeamWizardProps {
  /** null/undefined = create a new team. A Team = resume/enrich it. */
  initialTeam?: Team | null;
  /** Resume mode: jump straight to this step. */
  startStep?: WizardStep;
  onClose: () => void;
  /** Called after each persist so the parent can refresh its lists. */
  onChanged: () => void;
}

export default function TeamWizard({
  initialTeam = null,
  startStep = 'basics',
  onClose,
  onChanged,
}: TeamWizardProps) {
  const { features, plan } = useAuth();
  // When the Members spine is on, the wizard creates real coach Members +
  // guardian Members + guardianship links. When off, it degrades to the
  // legacy behaviour (team.coaches array + guardian on player consent).
  const membersEnabled = features.members === true;

  const [team, setTeam] = useState<Team | null>(initialTeam);
  const [step, setStep] = useState<WizardStep>(startStep);
  const [isSaving, setIsSaving] = useState(false);
  // True while ANY photo (team or coach) is uploading — gates the
  // step's primary button so a half-uploaded URL can't be saved.
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');

  // Basics form
  const [name, setName] = useState(initialTeam?.name || '');
  const [ageGroup, setAgeGroup] = useState(initialTeam?.ageGroup || '');
  const [description, setDescription] = useState(initialTeam?.description || '');
  const [image, setImage] = useState(initialTeam?.image || '');

  // Coaches
  const [coaches, setCoaches] = useState<Coach[]>(initialTeam?.coaches || []);

  // Squad
  const [squadText, setSquadText] = useState('');
  const [squadResult, setSquadResult] = useState<{
    added: number; failed: number; guardians?: number; notAdded: string[]; reason: string; capped: boolean;
  } | null>(null);
  const [clubPlayers, setClubPlayers] = useState<Player[] | null>(null);
  const [clubLimit, setClubLimit] = useState<number | null | undefined>(undefined);
  const room = clubPlayers ? playerRoom(plan, clubPlayers, clubLimit) : null;
  const pasted = parseSquadInput(squadText).length;
  const overRoom = !!room && room.capped && pasted > room.left;

  useEffect(() => {
    if (step !== 'squad' || clubPlayers) return;
    getPlayersAndLimit().then(r => { setClubLimit(r.playerLimit); setClubPlayers(r.players); }).catch(() => {});
  }, [step, clubPlayers]);

  const isResume = !!initialTeam;

  async function saveBasics() {
    if (!name.trim()) { setError('Team name is required.'); return; }
    setError('');
    setIsSaving(true);
    try {
      if (team) {
        // Resume / edit — update the existing team's basics.
        await updateTeam(team.id, { name: name.trim(), ageGroup, description, image });
        setTeam({ ...team, name: name.trim(), ageGroup, description, image });
      } else {
        // Create — persist immediately so skipping later is safe.
        const created = await createTeam({
          name: name.trim(),
          ageGroup,
          coach: '',
          coaches: [],
          description,
          image,
        });
        // Guard: never advance without a real id. A missing id would
        // make later updateTeam(undefined) UPSERT a junk "undefined"
        // team (the bug that lost coaches to a phantom team).
        if (!created?.id) {
          setError('Team was created but no id came back — please refresh and try again.');
          return;
        }
        setTeam(created);
      }
      onChanged();
      setStep('coaches');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the team.');
    } finally {
      setIsSaving(false);
    }
  }

  async function saveCoaches() {
    if (!team?.id) { setError('Team not saved yet — go back to step 1.'); return; }
    setError('');
    setIsSaving(true);
    try {
      // Drop empty coach rows (an "+ Add coach" with no name typed).
      const cleaned = coaches.filter(c => c.name.trim());
      if (membersEnabled) {
        // Create real coach Members; createCoach mirrors each into
        // team.coaches for us, so we don't also updateTeam here. Skip rows
        // already backed by a member (id starts 'mbr-') to avoid dupes on
        // resume.
        for (const c of cleaned) {
          if (c.id && c.id.startsWith('mbr-')) continue;
          await createCoach({ name: c.name.trim(), image: c.image || '', teamIds: [team.id] });
        }
      } else {
        await updateTeam(team.id, { coaches: cleaned });
        setTeam({ ...team, coaches: cleaned });
      }
      setCoaches(cleaned);
      onChanged();
      setStep('squad');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save coaches.');
    } finally {
      setIsSaving(false);
    }
  }

  async function finishSquad() {
    if (!team?.id) { setError('Team not saved yet — go back to step 1.'); return; }
    const rows = parseSquadInput(squadText);
    if (rows.length === 0) { setStep('done'); return; }
    setError('');
    setIsSaving(true);
    let added = 0, failed = 0, guardians = 0, capped = false, reason = '';
    const notAdded: string[] = [];

    // When the Members spine is on, dedupe guardian Members within this
    // run (and against existing members) so one parent with several kids
    // in the same paste becomes ONE member covering them all.
    const guardianByEmail: Record<string, string> = {};
    const guardianByName: Record<string, string> = {};
    if (membersEnabled) {
      try {
        for (const m of await getMembers()) {
          if (m.email) guardianByEmail[m.email.toLowerCase()] = m.memberId;
          else if (m.name) guardianByName[m.name.toLowerCase()] = m.memberId;
        }
      } catch { /* dedupe is best-effort */ }
    }

    for (const [i, p] of rows.entries()) {
      try {
        // Guardian name/email always land in the player's consent fields
        // (parentName/parentEmail) — the persisted safeguarding artifact.
        // Players are private by default, so partial consent here doesn't
        // trip the public-visibility gate.
        const consent = (p.guardianName || p.guardianEmail)
          ? { parentName: p.guardianName || undefined, parentEmail: p.guardianEmail || undefined }
          : undefined;
        const created = await createPlayer({
          firstName: p.firstName,
          lastName: p.lastName,
          teamId: team.id,
          ...(consent ? { consent } : {}),
        });
        added++;

        // Members spine on → also create/dedupe a real guardian Member +
        // link it to the player. Best-effort: a failure here doesn't undo
        // the player (and the consent fields still hold the contact).
        if (membersEnabled && created?.playerId && (p.guardianName || p.guardianEmail)) {
          try {
            const email = (p.guardianEmail || '').trim().toLowerCase();
            const name = (p.guardianName || '').trim();
            let memberId =
              (email && guardianByEmail[email]) ||
              (!email && name && guardianByName[name.toLowerCase()]) || '';
            if (!memberId) {
              const m = await createMember({
                name: name || email || 'Guardian',
                email: email || undefined,
                ageType: 'adult',
                roles: ['guardian'],
              });
              memberId = m.memberId;
              if (email) guardianByEmail[email] = memberId;
              else if (name) guardianByName[name.toLowerCase()] = memberId;
            }
            await linkGuardian(created.playerId, memberId, 'primary');
            guardians++;
          } catch { /* guardian link is best-effort */ }
        }
      } catch (e) {
        // Over the plan's cap every later row fails the same way, so stop.
        if (isPlayerLimitError(e)) {
          capped = true;
          notAdded.push(...rows.slice(i).map(squadLine));
          break;
        }
        failed++;
        notAdded.push(squadLine(p));
        if (!reason && e instanceof Error) reason = e.message;
      }
    }
    setSquadResult({ added, failed, guardians, notAdded, reason, capped });
    onChanged();
    setIsSaving(false);
    setStep('done');
  }

  const STEP_ORDER: WizardStep[] = ['basics', 'coaches', 'squad'];
  const stepIndex = STEP_ORDER.indexOf(step);

  return (
    // No overlay-click-to-close: a multi-step wizard holds typed-but-
    // unsaved input (coach rows, pasted squad). Closing is an explicit
    // action via the × / Cancel / Done buttons. Everything already
    // saved persists; only the current step's unsaved input is dropped,
    // and only on a deliberate close.
    <div className="tmw-overlay">
      <div className="tmw-modal">
        <div className="tmw-header">
          <div>
            <h2 className="tmw-title">
              {isResume ? `Set up ${initialTeam?.name || 'team'}` : 'Add a team'}
            </h2>
            {step !== 'done' && (
              <div className="tmw-steps" aria-hidden="true">
                {['Basics', 'Coaches', 'Squad'].map((label, i) => (
                  <span
                    key={label}
                    className={`tmw-step-pip ${i === stepIndex ? 'current' : i < stepIndex ? 'done' : ''}`}
                  >
                    {label}
                  </span>
                ))}
              </div>
            )}
          </div>
          <button className="tmw-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        {error && <div className="ealert ealert-error">{error}</div>}

        {/* ── Step 1: Basics ─────────────────────── */}
        {step === 'basics' && (
          <>
            <div className="tmw-step-body">
              <div className="tmw-field">
                <label>Team name *</label>
                <input
                  type="text"
                  className="tmw-input"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="e.g. U13 Lions"
                  autoFocus
                />
              </div>
              <div className="tmw-field">
                <label>Age group</label>
                <input
                  type="text"
                  className="tmw-input"
                  value={ageGroup}
                  onChange={e => setAgeGroup(e.target.value)}
                  placeholder="e.g. U13, Adults (optional)"
                />
              </div>
              <div className="tmw-field">
                <label>Description</label>
                <textarea
                  className="tmw-input"
                  rows={3}
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder="Training times, achievements… (optional)"
                />
              </div>
              <div className="tmw-field">
                <label>Team photo</label>
                <ImageUploader
                  onUpload={setImage}
                  currentImage={image}
                  label="Team Image"
                  onUploadingChange={setIsUploading}
                />
              </div>
            </div>
            <div className="tmw-footer">
              <button className="ebtn ebtn-ghost" onClick={onClose}>Cancel</button>
              <button
                className="ebtn ebtn-primary"
                onClick={saveBasics}
                disabled={isSaving || isUploading}
              >
                {isUploading ? 'Uploading image…' : isSaving ? 'Saving…' : 'Next: coaches →'}
              </button>
            </div>
          </>
        )}

        {/* ── Step 2: Coaches ────────────────────── */}
        {step === 'coaches' && (
          <>
            <CoachesStep coaches={coaches} onChange={setCoaches} onUploadingChange={setIsUploading} />
            <div className="tmw-footer">
              <button className="ebtn ebtn-ghost" onClick={() => setStep('basics')}>← Back</button>
              <div className="tmw-footer-right">
                <button className="ebtn ebtn-secondary" onClick={() => setStep('squad')} disabled={isSaving || isUploading}>
                  Skip for now
                </button>
                <button className="ebtn ebtn-primary" onClick={saveCoaches} disabled={isSaving || isUploading}>
                  {isUploading ? 'Uploading photo…' : isSaving ? 'Saving…' : 'Next: squad →'}
                </button>
              </div>
            </div>
          </>
        )}

        {/* ── Step 3: Squad ──────────────────────── */}
        {step === 'squad' && (
          <>
            <SquadStep value={squadText} onChange={setSquadText} teamName={team?.name}
                       room={room?.capped ? room.left : undefined} />
            {overRoom && room && (
              <div className="ealert ealert-info tmw-limit">
                <div>
                  {room.left === 0
                    ? <>Your {room.planLabel} plan is full: {room.used} of {room.limit} active players. Archive anyone who has left the club to make room.</>
                    : <>Your {room.planLabel} plan has room for {room.left} more player{room.left === 1 ? '' : 's'} ({room.used} of {room.limit} used), so only the first {room.left} will be added.</>}
                  {' '}{room.upgradeLine}.
                </div>
                <Link to="/upgrade" className="ebtn ebtn-primary ebtn-sm">Upgrade</Link>
              </div>
            )}
            <div className="tmw-footer">
              <button className="ebtn ebtn-ghost" onClick={() => setStep('coaches')}>← Back</button>
              <div className="tmw-footer-right">
                <button className="ebtn ebtn-secondary" onClick={() => setStep('done')} disabled={isSaving}>
                  Skip for now
                </button>
                <button className="ebtn ebtn-primary" onClick={finishSquad} disabled={isSaving || (overRoom && room?.left === 0)}>
                  {isSaving ? 'Adding players…' : 'Finish'}
                </button>
              </div>
            </div>
          </>
        )}

        {/* ── Done summary ───────────────────────── */}
        {step === 'done' && (
          <>
            <div className="tmw-step-body tmw-done">
              <div className="tmw-done-icon">✓</div>
              <h3>{team?.name} is set up</h3>
              <ul className="tmw-done-list">
                <li>{coaches.filter(c => c.name.trim()).length} coach(es)</li>
                <li>
                  {squadResult
                    ? `${squadResult.added} player(s) added${squadResult.failed ? `, ${squadResult.failed} failed` : ''}`
                    : 'No squad added yet'}
                </li>
                {squadResult?.capped && (
                  <li>
                    {squadResult.notAdded.length - squadResult.failed} not added: your {room?.planLabel ?? 'current'} plan
                    {room ? ` allows up to ${room.limit} active players` : ' is full'}
                  </li>
                )}
                {squadResult?.reason && <li>Why some failed: {squadResult.reason}</li>}
                {squadResult && squadResult.guardians ? (
                  <li>{squadResult.guardians} guardian(s) linked</li>
                ) : null}
              </ul>
              {squadResult && squadResult.notAdded.length > 0 && (
                <div className="tmw-not-added">
                  <p className="tmw-step-hint">
                    These didn't go in. Copy them now. Once there's room, open the team and paste them into the squad step.
                  </p>
                  <textarea
                    className="tmw-squad-textarea"
                    readOnly
                    rows={Math.min(8, squadResult.notAdded.length)}
                    value={squadResult.notAdded.join('\n')}
                    aria-label="Players not added"
                    onFocus={e => e.currentTarget.select()}
                  />
                  {squadResult.capped && room?.upgradeLine && (
                    <p className="tmw-step-hint">
                      {room.upgradeLine}, or archive anyone who has left the club.{' '}
                      <Link to="/upgrade" className="ebtn ebtn-primary ebtn-sm">Upgrade</Link>
                    </p>
                  )}
                </div>
              )}
              <p className="tmw-step-hint">
                You can add coaches or players any time — open the team and pick
                up where you left off.
              </p>
            </div>
            <div className="tmw-footer">
              <span />
              <button className="ebtn ebtn-primary" onClick={onClose}>Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
